import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Blocks,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  MessageCircle,
  Sparkles,
  Zap,
} from 'lucide-react'
import { CARD_STYLE, COLORS } from '../shared'
import KnowledgeSetup from './setup/KnowledgeSetup'
import TemplateSetup from './setup/TemplateSetup'
import IntegrationsSetup from './setup/IntegrationsSetup'

function SetupCard(props) {
  const {
    title,
    description,
    status,
    accent,
    open,
    onToggle,
    onLater,
    laterPath,
    children,
  } = props
  const CardIcon = props.icon
  return (
    <section
      className="overflow-hidden rounded-2xl transition-all duration-300 sm:rounded-3xl"
      style={{
        background: open ? 'rgba(26, 14, 36, 0.82)' : 'rgba(26, 14, 36, 0.5)',
        border: open ? `1px solid ${accent}66` : '1px solid rgba(255,255,255,0.07)',
        boxShadow: open ? `0 0 36px ${accent}16` : 'none',
      }}
    >
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ background: `${accent}18`, border: `1px solid ${accent}35` }}
          >
            <CardIcon className="h-5 w-5" style={{ color: accent }} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold" style={{ color: COLORS.text }}>
                {title}
              </span>
              {status && (
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{ background: `${accent}16`, color: accent, border: `1px solid ${accent}30` }}
                >
                  {status}
                </span>
              )}
            </span>
            <span className="mt-1 block text-xs leading-relaxed" style={{ color: COLORS.textMuted }}>
              {description}
            </span>
            <span className="mt-1.5 block text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.34)' }}>
              Depois: {laterPath}
            </span>
          </span>
          <ChevronDown
            className="h-4 w-4 shrink-0 transition-transform duration-200"
            style={{ color: 'rgba(255,255,255,0.42)', transform: open ? 'rotate(180deg)' : undefined }}
          />
        </button>

        <button
          type="button"
          onClick={onLater}
          className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors hover:bg-white/5 sm:self-auto"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          Ajustar depois
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>

      {open && (
        <div className="border-t px-4 pb-5 pt-4 sm:px-5 sm:pb-6" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <p className="mb-4 text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.38)' }}>
            Esta configuração é opcional. Para ajustar no futuro, abra <span style={{ color: 'rgba(255,255,255,0.68)' }}>{laterPath}</span>.
          </p>
          {children}
        </div>
      )}
    </section>
  )
}

export default function SetupHub({
  assistantID,
  user,
  setupProgress,
  channelStatus,
  onProgress,
  onChannelStatusChange,
  onTrack,
}) {
  const navigate = useNavigate()
  const [openSection, setOpenSection] = useState(null)
  const [focusedIntegration, setFocusedIntegration] = useState(null)

  useEffect(() => {
    onTrack?.('onboarding_setup_opened')
  }, [onTrack])

  function toggle(section) {
    setOpenSection((current) => (current === section ? null : section))
  }

  function openIntegration(type) {
    setFocusedIntegration(type)
    setOpenSection('integrations')
  }

  function goTo(path) {
    if (assistantID) navigate(`/assistants/${assistantID}/${path}`)
  }

  const knowledgeCount = Number(setupProgress?.knowledgeCount) || 0
  const templateCount = setupProgress?.templateIds?.length || 0

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 sm:gap-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest sm:text-[11px]"
          style={{
            background: 'rgba(173,52,254,0.12)',
            border: '1px solid rgba(173,52,254,0.28)',
            color: '#AD34FE',
          }}
        >
          <Sparkles className="h-3 w-3" />
          Preparação inicial
        </span>
        <h2 className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: COLORS.text, lineHeight: 1.1 }}>
          Deixe o agente mais útil desde o primeiro atendimento
        </h2>
        <p className="max-w-2xl text-sm leading-relaxed sm:text-base" style={{ color: COLORS.textMuted }}>
          Adicione conhecimento, instale capacidades e conecte os canais que fizerem sentido agora. Nada aqui é obrigatório.
        </p>
      </div>

      <div className="rounded-2xl p-1 sm:rounded-3xl" style={CARD_STYLE}>
        <div className="flex flex-col gap-2 p-3 sm:p-4">
          <SetupCard
            icon={BookOpen}
            title="Conhecimento"
            description="Treine com documentos, texto livre ou páginas do seu site."
            status={knowledgeCount ? `${knowledgeCount} ${knowledgeCount === 1 ? 'fonte' : 'fontes'}` : 'Opcional'}
            accent="#34ADFE"
            laterPath="Agente > Conhecimento"
            open={openSection === 'knowledge'}
            onToggle={() => toggle('knowledge')}
            onLater={() => goTo('knowledge')}
          >
            <KnowledgeSetup
              assistantID={assistantID}
              onProgress={onProgress}
              onTrack={onTrack}
            />
          </SetupCard>

          <SetupCard
            icon={Zap}
            title="Templates de gatilho"
            description="Instale automações prontas e evite começar os fluxos do zero."
            status={templateCount ? `${templateCount} ${templateCount === 1 ? 'instalado' : 'instalados'}` : 'Opcional'}
            accent="#FE9F34"
            laterPath="Agente > Gatilhos"
            open={openSection === 'templates'}
            onToggle={() => toggle('templates')}
            onLater={() => goTo('triggers')}
          >
            <TemplateSetup
              assistantID={assistantID}
              installedTemplateIds={setupProgress?.templateIds || []}
              onProgress={onProgress}
              onOpenIntegration={openIntegration}
              onTrack={onTrack}
            />
          </SetupCard>

          <SetupCard
            icon={MessageCircle}
            title="Canais"
            description="Conecte WhatsApp Oficial ou Evolution para receber conversas."
            status={channelStatus?.channelLabel || (channelStatus?.channel ? 'Conectado' : 'Opcional')}
            accent="#34FEAD"
            laterPath="Agente > Integrações"
            open={openSection === 'channels'}
            onToggle={() => toggle('channels')}
            onLater={() => goTo('integrations')}
          >
            <IntegrationsSetup
              assistantID={assistantID}
              user={user}
              mode="channels"
              onChannelStatusChange={onChannelStatusChange}
              onTrack={onTrack}
            />
          </SetupCard>

          <SetupCard
            icon={Blocks}
            title="Todas as integrações"
            description="Conecte CRM, agenda, catálogo, atendimento e outros serviços disponíveis."
            status="Catálogo completo"
            accent="#FE34AD"
            laterPath="Agente > Integrações"
            open={openSection === 'integrations'}
            onToggle={() => toggle('integrations')}
            onLater={() => goTo('integrations')}
          >
            <IntegrationsSetup
              assistantID={assistantID}
              user={user}
              mode="catalog"
              focusedIntegration={focusedIntegration}
              onChannelStatusChange={onChannelStatusChange}
              onTrack={onTrack}
            />
          </SetupCard>
        </div>
      </div>

      <p className="text-center text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.36)' }}>
        Você pode concluir agora e voltar a qualquer uma dessas opções no perfil do agente.
      </p>
    </div>
  )
}
